/**
 * Derivazioni pure della sezione Limulo: nessuna rete, nessun orologio letto di nascosto
 * (l'istante arriva come parametro), nessun `import.meta.env`. Sono le funzioni che
 * `tools/verify-limulo.ts` puo' esercitare sotto Node — e sono anche le uniche che
 * decidono cosa vede l'utente quando i dati non hanno la forma che ci si aspettava.
 *
 * La regola che percorre tutto il file: **cio' che non sappiamo non diventa un fatto.**
 * Una data illeggibile non diventa «non scade mai», un conteggio assente non diventa 0,
 * un identificatore di gioco non diventa un nome, un torneo che non capiamo non sparisce
 * dalla lista.
 */
import type {
  LimuloFavoriteGame,
  LimuloFavoriteGames,
  LimuloGame,
  LimuloMatch,
  LimuloMatchCheckin,
  LimuloMatchParticipant,
  LimuloMatchResult,
  LimuloMatchResultPayload,
  LimuloMediaAsset,
  LimuloPagination,
  LimuloParticipant,
  LimuloResultMediaAttach,
  LimuloSchedule,
  LimuloStandingsRow,
  LimuloTournament,
  LimuloWindow,
} from '../../types/limulo.ts';

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const asFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Il 2000 e il 2100 come estremi: fuori di li' non e' una data, e' un numero. */
const INSTANT_FLOOR = Date.UTC(2000, 0, 1);
const INSTANT_CEILING = Date.UTC(2100, 0, 1);

/**
 * Una data dal filo → epoch ms, oppure `null` («non lo so»).
 *
 * ⚠️ IL NUMERO NON E' IL «RAMO LEGACY»: e' anche il nativo. `app-qtournaments` dichiara
 * le date come numeri (`persistenza/tournament.model.js:383` →
 * `publishAt: { type: Number, default: null }`), le scrive come numeri
 * (`core/creazione.js:251`) e il sanitizer del BFF le passa senza convertirle
 * (`policy.js:263`: `tournament.schedule.publishAt || null`). Misurato su
 * `qlash-limulo@91743eb` — difetto segnalato da or35, prova aggiunta da tma-orcr.
 *
 * 🔴 E QUESTA FUNZIONE E' L'UNICA. Fino al 2026-09-02 ce n'erano DUE: questa, esportata,
 * con dodici asserzioni verdi — e un `instant()` privato che faceva
 * `Date.parse(asString(v))` e sui numeri restituiva `null`. Le funzioni vive usavano il
 * secondo. Il parser giusto non era mai stato collegato: il tipo ammetteva un caso che
 * il codice scartava, e il gate era verde su una funzione che nessuno chiamava.
 * Due parser per la stessa domanda sono un difetto anche quando entrambi sono corretti,
 * perche' basta che uno solo venga usato.
 *
 * ⚠️ ASSUNZIONE DICHIARATA: il numero e' interpretato come **millisecondi**. E' la
 * convenzione JS ed e' cio' che usa il provider, che confronta `publishAt` con
 * `Date.now()`. Se un giorno arrivassero SECONDI, cadrebbero nel 1970 — e la finestra
 * di sanita' li rende `null` invece di lasciarli diventare una data assurda.
 * La scelta e' deliberata e ha un costo: un `null` e' silenzioso, un «gennaio 1970» a
 * schermo salterebbe all'occhio subito. Si preferisce il silenzio perche' `null` qui non
 * e' l'assenza di una risposta — e' la risposta «non lo so», che la UI mostra come «date
 * non dichiarate» su OGNI riga; se succedesse davvero, sarebbe visibile lo stesso, senza
 * dire all'utente una cosa falsa nel frattempo.
 */
export function normalizeInstant(value: unknown): number | null {
  const ms =
    typeof value === 'number'
      ? value
      : Number.isFinite(Date.parse(asString(value)))
        ? Date.parse(asString(value))
        : NaN;
  if (!Number.isFinite(ms)) return null;
  return ms >= INSTANT_FLOOR && ms < INSTANT_CEILING ? ms : null;
}

// ── L'envelope ───────────────────────────────────────────────────────────────
// `{ok, organizationId, data, meta}` e, per le liste, `data: {items: [...]}` con la
// `pagination` FUORI da `data` (`router.js:73-92`). Nessuno degli altri client di questa
// app ha questa forma: non c'e' un unwrapper esistente da riusare.

export function unwrapData<T>(envelope: unknown): T | null {
  const record = asRecord(envelope);
  return 'data' in record ? (record.data as T) : null;
}

export function unwrapItems<T>(envelope: unknown): T[] {
  const items = asRecord(asRecord(envelope).data).items;
  return Array.isArray(items) ? (items as T[]) : [];
}

/**
 * ⚠️ NESSUN CONSUMATORE OGGI, e la distinzione conta: questa e' una **capacita' in attesa
 * di una superficie**, non un residuo. La forma e' quella che il BFF manda davvero (v. il
 * riferimento qui sotto); manca la schermata che la mostri. Chi la trova non la cancelli
 * come codice morto: «non ancora usata» e «non piu' usata» si assomigliano e portano a
 * decisioni opposte. (Controllo del 2026-09-02.)
 *
 * `buildPagination` (`router.js:121-131`) lascia `total` e `totalPages` `undefined` quando
 * il provider non li manda. Diventano `null`, che e' una domanda senza risposta: mostrare
 * «pagina 2 di 1» sarebbe peggio che non mostrare niente.
 */
export function unwrapPagination(envelope: unknown): LimuloPagination | null {
  const raw = asRecord(envelope).pagination;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const pagination = raw as Record<string, unknown>;
  return {
    page: asFiniteNumber(pagination.page) ?? 1,
    limit: asFiniteNumber(pagination.limit) ?? 0,
    total: asFiniteNumber(pagination.total),
    totalPages: asFiniteNumber(pagination.totalPages),
  };
}

// ── Il torneo ────────────────────────────────────────────────────────────────

/**
 * `sanitizePublicTournament` (`policy.js:229-237`) emette sia `title` sia `name`, ognuno
 * col fallback sull'altro. Sono quasi sempre uguali; quando non lo sono serve una regola
 * fissa, altrimenti due schermate mostrano due nomi diversi per lo stesso torneo.
 */
export function tournamentTitle(tournament: unknown): string {
  const t = asRecord(tournament);
  return asString(t.title) || asString(t.name);
}

/**
 * ⚠️ PASSTHROUGH, vincolo kb#81. Si restituisce l'identificatore di gioco COSI' COM'E',
 * senza risolverlo contro `app-games`: una seconda chiamata per trasformarlo in un nome
 * inventerebbe un legame che il contratto non dichiara, e legherebbe questa sezione a
 * un'app che oggi puo' essere spenta.
 *
 * E non si ripiega sul `gameMode`: mostrare «1v1» dove l'utente si aspetta «Clash Royale»
 * non e' un fallback, e' un'altra informazione.
 */
export function tournamentGameRef(tournament: unknown): string {
  return asString(asRecord(tournament).gameId);
}

const scheduleOf = (tournament: unknown): LimuloSchedule =>
  asRecord(asRecord(tournament).schedule) as LimuloSchedule;

/**
 * ⚠️ Senza `publishAt` la risposta e' «si mostra». La lista che chiediamo al BFF e' gia'
 * quella pubblica: filtrare qui su un campo assente nasconderebbe righe che il server ha
 * deliberatamente incluso — e un elenco che si accorcia da solo e' indistinguibile da un
 * backend vuoto.
 */
export function isPublished(tournament: unknown, now: number): boolean {
  const publishAt = normalizeInstant(scheduleOf(tournament).publishAt);
  return publishAt === null || publishAt <= now;
}

const windowBetween = (openAt: unknown, closeAt: unknown, now: number): LimuloWindow => {
  const open = normalizeInstant(openAt);
  const close = normalizeInstant(closeAt);
  if (open === null && close === null) return 'unknown';
  if (open !== null && now < open) return 'not-yet';
  if (close !== null && now >= close) return 'closed';
  return 'open';
};

export function registrationWindow(tournament: unknown, now: number): LimuloWindow {
  const schedule = scheduleOf(tournament);
  return windowBetween(schedule.registrationOpenAt, schedule.registrationCloseAt, now);
}

/**
 * 🔴 LO STATO D'ISCRIZIONE SECONDO IL SERVER — e **non** secondo il calendario.
 *
 * ⚠️ MISURATO IL 2026-09-06 SU `stg-tma` (or45), ed e' un difetto costato subito: per
 * `ciaocup-ca6975b3` la card mostrava «Iscrizioni: Aperte» — calcolato da
 * `registrationWindow`, cioe' da `registrationOpenAt` (14:56Z, gia' passata) — mentre il
 * server rispondeva `registration: { open: false, status: "scheduled", reason:
 * "window.before-open" }`. **Client e server affermavano stati opposti nello stesso
 * istante**, e lo schermo invitava a un'azione che il backend rifiuta — col 424 muto per
 * giunta.
 *
 * La causa non e' un errore di calcolo: la finestra la decide **anche lo stato del torneo**,
 * non solo le date. `finestraDi` (`qTournaments core/iscrizioni.ts:340-362`) chiude su
 * `classe === 'prima'` PRIMA di guardare `registrationOpenAt` — e su staging lo scheduler non
 * aveva mai fatto avanzare il torneo da `published`. Il calendario, da solo, non poteva
 * saperlo.
 *
 * 🔑 E il contratto lo dice espressamente. Da `registrazioneDa`
 * (`superficie/capability/tornei.ts:174-195`): *«`open` e' ridondante con `status === 'open'`
 * per chi legge, e resta perche' e' **la domanda vera**: un client che deve decidere se
 * mostrare il pulsante non deve dedurla da un'enumerazione.»* Il campo esiste APPOSTA perche'
 * noi non deduciamo.
 *
 * ⇒ **Il calendario e' informazione («apre alle …»), non un verdetto.** Quando il server non
 * ha parlato la risposta e' `unknown`, mai `open`: affermare un'apertura che nessuno ha
 * dichiarato e' la stessa famiglia del vuoto affermato che questa sezione paga da sempre.
 *
 * Il vocabolario di `status` e' quello di `StatoFinestra` (`core/iscrizioni.ts:130-150`):
 * `not_published` · `scheduled` · `open` · `closed` · `full`.
 */
export type LimuloRegistrationState =
  | 'open'
  | 'scheduled'
  | 'closed'
  | 'full'
  | 'not-published'
  /** Il server non ha mandato il blocco, o l'ha mandato senza `open` booleano. */
  | 'unknown';

const STATO_SERVER: Record<string, LimuloRegistrationState> = {
  not_published: 'not-published',
  scheduled: 'scheduled',
  closed: 'closed',
  full: 'full',
};

export function registrationStateOf(tournament: unknown): LimuloRegistrationState {
  const blocco = asRecord(asRecord(tournament).registration);
  const open = blocco.open;
  // ⚠️ Solo un BOOLEANO conta. Una stringa `"false"`, un `null` o l'assenza sono «non l'ha
  // detto» — e trattarli come una risposta significherebbe leggere un'ignoranza come un
  // fatto, che e' esattamente il difetto di cui sopra a parti invertite.
  if (typeof open !== 'boolean') return 'unknown';
  if (open) return 'open';
  // `open: false` E' gia' la risposta; `status` serve solo a dire QUALE chiusura. Uno
  // `status` che non conosciamo non annulla il `false`: resta una chiusura, senza il nome.
  return STATO_SERVER[asString(blocco.status)] ?? 'closed';
}

/**
 * ⚠️ Resta, e resta **informazione**: «le iscrizioni aprono alle …» e' una cosa utile da
 * mostrare. Non e' un verdetto e non decide nessun bottone — v. `registrationStateOf`.
 */
export function checkInWindow(tournament: unknown, now: number): LimuloWindow {
  const schedule = scheduleOf(tournament);
  return windowBetween(schedule.checkInOpenAt, schedule.checkInCloseAt, now);
}

/**
 * «12/64», «12», oppure niente. Mai «12/null» e mai «12/0»: un massimo a zero non e' un
 * massimo, e' un campo che il provider non ha compilato.
 *
 * Lo zero nel conteggio invece si mostra: zero iscritti e' un fatto, non un dato mancante.
 */
export function participantsLabel(tournament: unknown): string {
  const { iscritti, capienza } = participantsCounts(tournament);
  if (iscritti === null) return '';
  return capienza !== null && capienza > 0 ? `${iscritti}/${capienza}` : `${iscritti}`;
}

/**
 * Gli stessi due numeri di `participantsLabel`, **prima** che diventino una stringa.
 *
 * Esiste perche' il verdetto d'iscrizione (`eligibility.ts`) deve confrontarli, e
 * ricavarli da un'etichetta significherebbe fare il parsing di qualcosa che abbiamo
 * appena formattato noi — una misura che ha come prova il proprio soggetto.
 * ⚠️ `null` e' «non c'e' il dato», mai `0`: da un iscritto mancante non si deduce un
 * torneo vuoto.
 */
export function participantsCounts(tournament: unknown): {
  iscritti: number | null;
  capienza: number | null;
} {
  const t = asRecord(tournament);
  return {
    iscritti: asFiniteNumber(t.participantsCount),
    capienza: asFiniteNumber(asRecord(t.limits).maxPlayers),
  };
}

/**
 * Ordine della lista: prima cio' su cui si puo' agire adesso (iscrizioni aperte), poi cio'
 * che aprira', poi cio' che e' chiuso, e in fondo cio' di cui non sappiamo abbastanza.
 * Dentro ogni gruppo, per data d'inizio crescente.
 *
 * Non muta la lista in ingresso e non ne perde pezzi: un torneo che non capiamo finisce in
 * fondo, non fuori. Un ordinamento che scarta e' un filtro travestito.
 *
 * 🔴 **LA CHIAVE PRIMARIA E' LO STATO DEL SERVER, non il calendario** — corretto il
 * 2026-09-06, dopo il caso `ciaocup-ca6975b3` (v. `registrationStateOf`).
 *
 * Un ordinamento non e' una frase, ed e' proprio questo che lo rendeva facile da lasciare
 * indietro: nessuno lo legge come un'affermazione. Ma **la prima posizione di una lista e'
 * un consiglio**, e mettere in cima un torneo che il server dichiara `scheduled` dice
 * «comincia da qui» su una cosa su cui non si puo' agire. E' lo stesso difetto della card,
 * in forma attenuata — e attenuata non vuol dire assente.
 *
 * Il calendario resta come chiave **secondaria**, dentro lo stesso stato: fra due tornei che
 * il server dichiara allo stesso modo, e' un criterio ragionevole e non afferma niente che
 * il server abbia contraddetto.
 */
const REG_STATE_RANK: Record<LimuloRegistrationState, number> = {
  open: 0,
  scheduled: 1,
  // `closed` e `full` stanno insieme: sono due modi di non poter entrare, e distinguerli
  // nell'ordine non aiuta chi scorre — la differenza si legge nella riga, non nella posizione.
  closed: 2,
  full: 2,
  // ⚠️ `not-published` sta con `unknown` e non con `closed`: un torneo non pubblicato non e'
  // «chiuso», e' un torneo che ancora non riguarda nessuno. In fondo, come l'ignoranza.
  'not-published': 3,
  unknown: 3,
};

const WINDOW_RANK: Record<LimuloWindow, number> = {
  open: 0,
  'not-yet': 1,
  closed: 2,
  unknown: 3,
};

export function sortTournaments<T>(list: unknown, now: number): T[] {
  if (!Array.isArray(list)) return [];
  return [...(list as T[])]
    .map((tournament, index) => ({ tournament, index }))
    .sort((a, b) => {
      // 1. Cio' che il SERVER dichiara. ⛔ Non riportare qui `registrationWindow`: le date
      //    da sole non conoscono lo stato del torneo (v. il cappello di `REG_STATE_RANK`).
      const statoRank =
        REG_STATE_RANK[registrationStateOf(a.tournament)] -
        REG_STATE_RANK[registrationStateOf(b.tournament)];
      if (statoRank !== 0) return statoRank;
      // 2. Poi il calendario, ma solo DENTRO lo stesso stato: qui non contraddice nessuno.
      const rank =
        WINDOW_RANK[registrationWindow(a.tournament, now)] -
        WINDOW_RANK[registrationWindow(b.tournament, now)];
      if (rank !== 0) return rank;
      const startA = normalizeInstant(scheduleOf(a.tournament).startAt);
      const startB = normalizeInstant(scheduleOf(b.tournament).startAt);
      if (startA !== startB) {
        if (startA === null) return 1;
        if (startB === null) return -1;
        return startA - startB;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.tournament);
}

// ── Standings: l'unica rotta senza facciata ──────────────────────────────────

/**
 * `qlash.tournaments.standings.read` passa da `handleCapabilityJson`
 * (`router.js:1847-1853`) SENZA sanitizer: esce la forma grezza del provider, e non c'e'
 * un contratto da rispecchiare. Quindi si accettano le forme plausibili, si leggono i
 * nomi di campo piu' probabili, e soprattutto **non si scarta niente**: la riga originale
 * resta allegata in `raw`, cosi' una schermata puo' mostrare cio' che non avevamo
 * previsto invece di far finta che non esista.
 *
 * Una riga senza nome resta una riga: farla sparire farebbe sparire un concorrente.
 */
const firstArray = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw;
  const record = asRecord(raw);
  for (const key of ['items', 'standings', 'rows', 'results']) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }
  return [];
};

export function standingsRows(raw: unknown): LimuloStandingsRow[] {
  return firstArray(raw).map((entry) => {
    const row = asRecord(entry);
    return {
      position: asFiniteNumber(row.position) ?? asFiniteNumber(row.rank) ?? asFiniteNumber(row.place),
      name:
        asString(row.displayName) ||
        asString(row.username) ||
        asString(row.name) ||
        asString(row.participantName),
      points: asFiniteNumber(row.points) ?? asFiniteNumber(row.score),
      raw: row,
    };
  });
}

// ── Iscrizione ───────────────────────────────────────────────────────────────

export type { LimuloTournament };


// ── Il bordo largo, il centro stretto ────────────────────────────────────────
// ⚠️ PERCHE' ESISTE QUESTA SEZIONE: `app-tournaments` (il legacy) fornisce 5 delle 7
// capability di questa sezione a priority **205**, contro il **200** del provider
// nativo (`app-tournaments/manifest.js:249-268`). Oggi e' SPENTO, ma accenderlo
// cambierebbe forma e dati senza che nessuno tocchi il client: le date arriverebbero
// come numeri invece che come stringhe ISO, e i limiti porterebbero zeri legittimi.
//
// L'unione vive SOLO qui. Propagare `number|string|null` dentro le schermate
// significherebbe pagare complessita' per sempre contro un provider che potrebbe non
// essere acceso mai — e la complessita' gratuita e' essa stessa un difetto. Da questa
// riga in giu' esiste solo `number | null`.

/**
 * ⚠️ NESSUN CONSUMATORE OGGI, e la distinzione conta: questa e' una **capacita' in attesa
 * di una superficie**, non un residuo. La forma e' quella che il BFF manda davvero (v. il
 * riferimento qui sotto); manca la schermata che la mostri. Chi la trova non la cancelli
 * come codice morto: «non ancora usata» e «non piu' usata» si assomigliano e portano a
 * decisioni opposte. (Controllo del 2026-09-02.)
 *
 * Un limite dal filo → numero, oppure `null`.
 *
 * Lo **zero passa**: dal legacy e' un valore legittimo, non un campo mancante, e
 * trattarlo come assente cancellerebbe un dato vero. Il negativo no: una capienza
 * negativa non e' un fatto, e' spazzatura. Chi renderizza decide poi cosa farne — e
 * `participantsLabel` continua a non scrivere «12/0», che non e' mai giusto.
 */
export function normalizeLimit(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return value;
}

/**
 * Cosa mostrare per la modalita' di gioco.
 *
 * `label` PUO' essere la chiave macchina: `policy.js:248-252` la riempie con
 * `gameModeKey` nel ramo di fallback, e un provider puo' mandare `label === key`. Ma
 * puo' anche essere un'etichetta vera. La regola non e' «ignora label», e': **non
 * assumere che sia leggibile, ma non buttarla**.
 *
 * ⚠️ NESSUN CATALOGO DI CHIAVI NOTE, di proposito. Nel mirror non esiste una sola
 * chiave letterale — sono dati, non codice — quindi qualunque elenco sarebbe stato
 * inventato qui, cioe' un'osservazione spacciata per contratto. `i18nKey` e' percio' un
 * TENTATIVO di traduzione, sempre costruito dalla chiave, e `text` e' cio' che si
 * mostra se il tentativo non trova niente. Aggiungere una traduzione domani e'
 * additivo e non tocca questo file.
 */
export function gameModeDisplay(gameMode: unknown): { i18nKey: string | null; text: string } {
  const mode = asRecord(gameMode);
  const key = asString(mode.key);
  const label = asString(mode.label);
  if (!key) return { i18nKey: null, text: label };
  return { i18nKey: `gameMode.${key}`, text: label || key };
}


/**
 * Lo stato d'iscrizione, con la sua inversione dichiarata.
 *
 * ⚠️ `registration` E' PRESENTE ESATTAMENTE QUANDO NON SEI ISCRITTO.
 * `superficie/capability/iscrizioni.js:190-220`:
 *
 *   NON iscritto → status 'not_registered', registered false, registration {finestra}, party null
 *       ISCRITTO → status 'registered',     registered true,  registration NULL,       party {…}
 *
 * Non e' «a volte manca»: c'e' nel caso opposto a quello in cui servirebbe. E poi
 * `sanitizeRegistrationStatus` (`policy.js:380-389`) fa cadere `party`, quindi del caso
 * «iscritto» al client resta solo `registered` + `participant`.
 *
 * Da qui `hasWindow`: un flag esplicito invece di lasciare che una schermata provi a
 * leggere una finestra che, proprio quando serve, non c'e'. La finestra si legge dalla
 * scheda torneo.
 *
 * ⚠️ E vale anche come rete per la SPEC B: da una riga di `/v1/tournaments/mine` non si
 * puo' dedurre nulla sull'iscrizione — `party` cade anche li' (`policy.js:229`) e la card
 * «mio torneo» e' identica a una pubblica. Chi passasse una riga di torneo a questa
 * funzione ottiene «no», che e' la verita', non una bugia comoda.
 */
export function registrationInfo(raw: unknown): {
  registered: boolean;
  status: string;
  hasWindow: boolean;
} {
  const record = asRecord(raw);
  return {
    registered: record.registered === true,
    status: asString(record.status),
    hasWindow: Object.keys(asRecord(record.registration)).length > 0,
  };
}

/**
 * La classifica, coi due campi che la facciata non ha mai promesso e che il provider
 * manda comunque (`classifica.js:185-201`): `final` e `unavailable`.
 *
 * ⚠️ `items: []` CON `unavailable` NON E' «nessun risultato»: e' una lista vuota che sa
 * spiegarsi. Trattarli allo stesso modo e' il finto vuoto che questa sezione esiste per
 * non produrre — da qui `isEmpty`, che e' vero solo quando il vuoto e' davvero un vuoto.
 *
 * ⚠️ `final` assente diventa `null` e non `false`: «non lo so» e «non e' definitiva» sono
 * cose diverse, e mostrare un parziale con la faccia dell'ufficiale e' il danno peggiore
 * che una classifica possa fare.
 */
export function standingsOf(raw: unknown): {
  rows: LimuloStandingsRow[];
  unavailable: unknown[] | null;
  final: boolean | null;
  total: number | null;
  isEmpty: boolean;
} {
  const record = asRecord(raw);
  const rows = standingsRows(raw);
  const rawUnavailable = record.unavailable;
  const unavailable =
    Array.isArray(rawUnavailable) && rawUnavailable.length > 0 ? (rawUnavailable as unknown[]) : null;
  return {
    rows,
    unavailable,
    final: typeof record.final === 'boolean' ? record.final : null,
    total: asFiniteNumber(record.total),
    isEmpty: rows.length === 0 && unavailable === null,
  };
}

/**
 * I requisiti d'iscrizione — che NON stanno dove il nome dell'endpoint farebbe pensare.
 *
 * `sanitizeRegistrationRequirements` (`policy.js:391-402`) e' un letterale a cinque chiavi
 * SENZA spread: i dettagli veri (partySize, groupEntry, livelli) non passano da
 * `data.registration` ma dal passthrough `tournament.requirements`
 * (`sanitizePublicTournament`, `policy.js:282-285`) — e il provider nativo lo dice
 * esplicitamente nel proprio commento (`superficie/capability/iscrizioni.js:133-145`).
 * Dal legacy quella chiave non esiste: opzionale, mai data per scontata.
 *
 * `profile` e' passthrough grezzo di un provider TERZO (`policy.js:397`): non ha una forma
 * dichiarata da nessuno, quindi non se ne inventa una qui.
 */
export function requirementsOf(raw: unknown): {
  requirements: Record<string, unknown> | null;
  requiredCapabilities: string[];
  profile: unknown;
  /**
   * ⚠️ Il profilo di gioco a TRE valori, e la terza via non e' pedanteria.
   *
   * `profile` da solo collassa due cose diverse: **«il server ha risposto: nessun
   * profilo»** e **«il server non ha detto niente»**. La prima autorizza a dire
   * all'utente cosa gli manca; la seconda no — e oggi e' il caso normale, perche' la
   * rotta del profilo di gioco risponde `424` e il campo non arriva affatto.
   *
   * · `true`  — la chiave c'e' e porta qualcosa ⇒ un profilo esiste;
   * · `false` — la chiave c'e' ed e' esplicitamente `null` ⇒ il server nega;
   * · `null`  — la chiave **non c'e'** ⇒ non lo sappiamo, e non si afferma.
   *
   * 📌 *Un `?? null` e' una perdita di informazione che non lascia traccia: il campo
   * assente e il campo negato escono identici, e a valle nessuno puo' piu' distinguerli.*
   */
  haProfiloDiGioco: boolean | null;
} {
  const record = asRecord(raw);
  const tournament = asRecord(record.tournament);
  const requirements =
    tournament.requirements !== null &&
    typeof tournament.requirements === 'object' &&
    !Array.isArray(tournament.requirements)
      ? (tournament.requirements as Record<string, unknown>)
      : null;
  const profileDichiarato = Object.prototype.hasOwnProperty.call(record, 'profile');
  return {
    requirements,
    requiredCapabilities: asStringArray(record.requiredCapabilities),
    profile: record.profile ?? null,
    haProfiloDiGioco: profileDichiarato ? record.profile != null : null,
  };
}

/**
 * ⚠️ NESSUN CONSUMATORE OGGI, e la distinzione conta: questa e' una **capacita' in attesa
 * di una superficie**, non un residuo. La forma e' quella che il BFF manda davvero (v. il
 * riferimento qui sotto); manca la schermata che la mostri. Chi la trova non la cancelli
 * come codice morto: «non ancora usata» e «non piu' usata» si assomigliano e portano a
 * decisioni opposte. (Controllo del 2026-09-02.)
 *
 * Gli iscritti come la facciata li consegna — cioe' **senza avatar**.
 * `sanitizePublicParticipant` (`policy.js:218-227`) non emette `avatarUrl`, che il
 * provider nativo invece produce; nei match gli avatar ci sono, qui no. La risposta
 * giusta e' mostrarli senza: una chiamata al profilo per riga sarebbe un N+1 su una
 * schermata di lista, per un dato che la facciata ha deciso di non dare.
 *
 * ⚠️ `seed` e `rank` si PASSANO. Oggi il provider li lascia a null, ma il sanitizer non
 * li forza (`?? null`): «sempre null» e' un'osservazione, non un contratto, e inciderla
 * qui produrrebbe un rosso nostro e falso il giorno che vengono popolati.
 */
export function participantsOf(raw: unknown): LimuloParticipant[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const p = asRecord(entry);
    return {
      id: asString(p.id) || asString(p.participantId),
      participantId: asString(p.participantId) || asString(p.id),
      displayName: asString(p.displayName),
      username: asString(p.username),
      status: asString(p.status),
      seed: asFiniteNumber(p.seed),
      rank: asFiniteNumber(p.rank),
      addedAt: asString(p.addedAt) || null,
    };
  });
}


// ── Fase 3: l'incontro, il referto, i preferiti ──────────────────────────────
// Le derivazioni delle rotte aggiunte al client (`services/limuloApi.ts`). Stessa regola
// del resto del file: cio' che il server non ha detto non diventa un fatto — e la forma
// esce SEMPRE intera, cosi' una schermata non deve difendersi da campi che mancano.

const asBoolean = (value: unknown): boolean => value === true;

/** `undefined` non e' `false`: «non l'ha detto» e «non e' successo» sono due cose. */
const asBooleanOrNull = (value: unknown): boolean | null =>
  typeof value === 'boolean' ? value : null;

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/**
 * Il partecipante dentro un incontro: `sanitizeMatchParticipant` (`policy.js:420-427`).
 * `null` quando la parte non c'e' — un oggetto coi campi vuoti sembrerebbe un giocatore
 * senza nome, mentre `null` dice «non c'e'» (bye e forfeit hanno una parte sola).
 */
function matchParticipantOf(raw: unknown): LimuloMatchParticipant | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const p = asRecord(raw);
  const id = asString(p.id) || asString(p.participantId);
  return {
    id,
    participantId: asString(p.participantId) || id,
    displayName: asString(p.displayName),
    username: asString(p.username),
    status: asString(p.status),
    seed: asFiniteNumber(p.seed),
    rank: asFiniteNumber(p.rank),
    addedAt: asString(p.addedAt) || null,
    profileId: asString(p.profileId),
    avatarUrl: asString(p.avatarUrl),
  };
}

/** `sanitizeMediaAsset` (`policy.js:525-542`). */
function mediaAssetOf(raw: unknown): LimuloMediaAsset {
  const a = asRecord(raw);
  return {
    id: asString(a.id),
    assetId: asString(a.assetId) || asString(a.id),
    name: asString(a.name),
    originalName: asString(a.originalName),
    mimeType: asString(a.mimeType),
    mediaType: asString(a.mediaType),
    // ⚠️ I tre `?? 0` sono RIDONDANTI, e restano: il BFF appiattisce gia' a zero
    // (`policy.js:534-536` dentro `sanitizeMediaAsset`, `:525-542` @91743eb —
    // `size: asset.size ?? 0`). Qui e' difesa in profondita', non una scelta di
    // rappresentazione: se un giorno la risposta arrivasse da un percorso che quel
    // sanitizer non attraversa, un `undefined` diventerebbe `NaN` alla prima
    // formattazione. ⚠️ E NON si sostituiscono con `null`: il server manda `0`, e
    // rappresentare come «non lo so» un valore che il server ha dichiarato sarebbe
    // scrivere una cosa diversa da quella ricevuta.
    // (Il difetto vero — `0` indistinguibile da «dimensione non nota» — e' del
    // contratto, non di questa riga: portato a Fabio come voce di kb#114.)
    size: asFiniteNumber(a.size) ?? 0,
    width: asFiniteNumber(a.width) ?? 0,
    height: asFiniteNumber(a.height) ?? 0,
    previewUrl: asString(a.previewUrl),
    downloadUrl: asString(a.downloadUrl),
    url: asString(a.url),
    sharedUrl: asString(a.sharedUrl),
  };
}

/**
 * Il referto: `sanitizeMatchResult` (`policy.js:429-451`). `null` a incontro non concluso
 * — che non e' un dato mancante, e' «non ancora refertato» (`incontri.js:145-150`).
 *
 * ⚠️ Il punteggio sono DUE numeri, non la stringa `"3:1"` del monolite. Il vincitore si
 * LEGGE (`winnerPlayerId`), non si ricalcola dal punteggio: il provider lo deriva da
 * `outcome` (`incontri.js:82-101`) e una seconda regola qui divergerebbe dalla sua.
 */
function matchResultOf(raw: unknown): LimuloMatchResult | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = asRecord(raw);
  return {
    matchId: asString(r.matchId),
    roundNumber: asFiniteNumber(r.roundNumber),
    playerA: asString(r.playerA),
    playerB: asString(r.playerB),
    scoreA: asFiniteNumber(r.scoreA),
    scoreB: asFiniteNumber(r.scoreB),
    winnerPlayerId: asString(r.winnerPlayerId) || null,
    resultType: asString(r.resultType),
    source: asString(r.source),
    reason: asString(r.reason),
    recordedAt: asString(r.recordedAt) || null,
    clientMutationId: asString(r.clientMutationId),
    evidenceMediaAssetIds: asStringArray(r.evidenceMediaAssetIds),
    mediaAssets: asList(r.mediaAssets).map(mediaAssetOf),
  };
}

/** `sanitizeMatchCheckin` (`policy.js:453-461`). */
function matchCheckinOf(raw: unknown): LimuloMatchCheckin {
  const c = asRecord(raw);
  const playerId = asString(c.playerId);
  return {
    playerId,
    participantId: asString(c.participantId) || playerId,
    profileId: asString(c.profileId),
    // Il server manda `checkedIn !== false`: una presenza registrata e' presente.
    checkedIn: c.checkedIn !== false,
    checkedInAt: asString(c.checkedInAt) || null,
    source: asString(c.source),
    clientMutationId: asString(c.clientMutationId),
  };
}

/**
 * L'incontro: `sanitizePublicMatch` (`policy.js:463-502`).
 *
 * ⚠️ `status` esce GREZZO. Il provider ne emette due soli — `pending`/`completed`
 * (`incontri.js:187`) — contro gli otto di `EMatchStatus` del monolite: tradurlo verso
 * quel vocabolario significherebbe inventare stati che il server non ha mai dichiarato,
 * e ogni `===` copiato da una schermata Laravel qui e' gia' falso.
 *
 * ⚠️ `roundNumber` assente resta `null` e non `1`: il `m.round || 1` del monolite
 * ammucchia tutti gli incontri in un «Round 1» che nessuno ha mai indetto.
 *
 * ⚠️ I tre `can*` mancanti diventano `false`. E' l'unico default sicuro: un pulsante
 * acceso per omissione invita a un'azione che il writer rifiutera'.
 */
export function matchOf(raw: unknown): LimuloMatch {
  const match = asRecord(raw);
  const players = asRecord(match.players);
  const matchId = asString(match.matchId) || asString(match.id);
  return {
    id: asString(match.id) || matchId,
    matchId,
    tournamentId: asString(match.tournamentId),
    roundNumber: asFiniteNumber(match.roundNumber),
    tableNumber: asFiniteNumber(match.tableNumber),
    status: asString(match.status),
    isBye: asBoolean(match.isBye),
    playerA: asString(match.playerA),
    playerB: asString(match.playerB),
    players: { a: matchParticipantOf(players.a), b: matchParticipantOf(players.b) },
    result: matchResultOf(match.result),
    externalMatch:
      match.externalMatch !== null &&
      typeof match.externalMatch === 'object' &&
      !Array.isArray(match.externalMatch)
        ? (() => {
            const e = asRecord(match.externalMatch);
            const id = asString(e.id) || asString(e.externalMatchId);
            return {
              id,
              externalMatchId: asString(e.externalMatchId) || id,
              providerKey: asString(e.providerKey),
              status: asString(e.status),
              instructions:
                e.instructions !== null &&
                typeof e.instructions === 'object' &&
                !Array.isArray(e.instructions)
                  ? (e.instructions as Record<string, unknown>)
                  : null,
              resultSyncStrategy: asString(e.resultSyncStrategy),
            };
          })()
        : null,
    winnerPlayerId: asString(match.winnerPlayerId) || null,
    checkins: asList(match.checkins).map(matchCheckinOf),
    canSubmitResult: asBoolean(match.canSubmitResult),
    canCheckIn: asBoolean(match.canCheckIn),
    canForfeit: asBoolean(match.canForfeit),
  };
}

/**
 * «I miei incontri», filtrati QUI e non dal server.
 *
 * ⚠️ IL MOTIVO, che e' una misura: la facciata SPOGLIA `participantId` dalla query
 * (`stripMatchClientFields`, `router.js:307-320`), quindi `?participant=<id>` — che sul
 * monolite funziona (`TournamentDetail.tsx:161-170`) — qui produce la lista INTERA con
 * 200. Un filtro che non filtra e non lo dice e' peggio di un filtro assente: chi guarda
 * conclude che quegli incontri sono i suoi.
 *
 * Senza un `participantId` si restituisce tutto: «non so chi sei» non puo' diventare
 * «non hai incontri», che a schermo e' una bugia con la faccia di un dato.
 */
export function matchesOfParticipant<T>(list: readonly T[], participantId: string | null | undefined): T[] {
  if (!Array.isArray(list)) return [];
  const id = asString(participantId);
  if (id === '') return [...list];
  return list.filter((entry) => {
    const players = asRecord(asRecord(entry).players);
    return (
      asString(asRecord(players.a).participantId) === id ||
      asString(asRecord(players.b).participantId) === id
    );
  });
}

/**
 * La risposta di `GET`/`POST …/result`, `…/checkin` e `…/forfeit`:
 * `sanitizeMatchResultPayload` (`policy.js:506-523`). Quattro rotte, una forma sola.
 *
 * ⚠️ `checkedIn` resta `null` quando il server non lo manda (`policy.js:510`: e'
 * `undefined`, non `false`). Su un check-in la differenza e' tutta: `false` direbbe
 * all'utente che la sua presenza NON e' stata registrata.
 */
export function matchResultPayloadOf(raw: unknown): LimuloMatchResultPayload {
  const p = asRecord(raw);
  const autoCompletion = asRecord(p.autoCompletion);
  return {
    match: p.match === null || p.match === undefined ? null : matchOf(p.match),
    result: matchResultOf(p.result),
    submitted: asBoolean(p.submitted),
    checkedIn: asBooleanOrNull(p.checkedIn),
    checkin:
      p.checkin === null || p.checkin === undefined ? null : matchCheckinOf(p.checkin),
    idempotent: asBoolean(p.idempotent),
    autoGeneratedRound: p.autoGeneratedRound ?? null,
    autoGeneration: p.autoGeneration ?? null,
    autoCompletion:
      p.autoCompletion === null || p.autoCompletion === undefined
        ? null
        : {
            completed: asBoolean(autoCompletion.completed),
            results: autoCompletion.results ?? null,
            error: autoCompletion.error ?? null,
          },
  };
}

/**
 * `POST …/result-media`: `sanitizeResultMediaAttachResult` (`policy.js:559-565`) — il
 * referto di cui sopra PIU' gli asset allegati.
 *
 * ⚠️ Questa rotta e' il SECONDO passo di un flusso a due, non una variante del referto:
 * il server registra l'asset (`media.asset.register`) e poi lo allega (`router.js:1940-1975`).
 * Sul monolite l'immagine si postava sullo stesso `/result` del punteggio: chi porta qui
 * quella schermata deve mandare prima il punteggio.
 */
export function resultMediaAttachOf(raw: unknown): LimuloResultMediaAttach {
  const p = asRecord(raw);
  return {
    ...matchResultPayloadOf(raw),
    assets: asList(p.assets).map(mediaAssetOf),
    attached: asBoolean(p.attached),
  };
}

/** Il gioco: `sanitizePublicGame` (`policy.js:152-163`). */
function gameOf(raw: unknown): LimuloGame {
  const g = asRecord(raw);
  const id = asString(g.id);
  return {
    id,
    gameId: asString(g.gameId) || id,
    name: asString(g.name) || asString(g.label),
    label: asString(g.label) || asString(g.name),
    profileType: asString(g.profileType),
    genre: asString(g.genre),
    websiteUrl: asString(g.websiteUrl),
    platformIds: asStringArray(g.platformIds),
    status: asString(g.status),
    tags: asStringArray(g.tags),
  };
}

/** Il preferito: `sanitizeFavoriteGame` (`policy.js:167-173`). */
function favoriteGameOf(raw: unknown): LimuloFavoriteGame {
  const f = asRecord(raw);
  const id = asString(f.id) || asString(f.favoriteId);
  return {
    id,
    favoriteId: asString(f.favoriteId) || id,
    gameId: asString(f.gameId),
    // `active !== false` e' la regola del server: un preferito e' attivo salvo revoca.
    active: f.active !== false,
    game: gameOf(f.game ?? f),
  };
}

/**
 * ⚠️ `GET /v1/me/favorite-games` risponde un OGGETTO — `{active, favorite, items, games}`
 * dentro `data` (`sanitizeFavoriteGameResult`, `policy.js:178-187`; l'handler usa `sendOk`,
 * non `sendListOk`: `router.js:1683-1685`). Srotolarlo con `unwrapItems` darebbe `[]` con
 * 200, cioe' «nessun preferito» dove la verita' e' «non hai guardato nel posto giusto».
 * E' l'unica delle quindici rotte che sembra una lista e non lo e'.
 */
export function favoriteGamesOf(raw: unknown): LimuloFavoriteGames {
  const f = asRecord(raw);
  return {
    active: asBooleanOrNull(f.active),
    favorite: f.favorite === null || f.favorite === undefined ? null : favoriteGameOf(f.favorite),
    items: asList(f.items).map(favoriteGameOf),
    games: asList(f.games).map(gameOf),
  };
}
